import React from 'react';
import { FormatNumberWithCurrencyTypeSchema } from '@kambi/betting-client-helpers';

import globalClasses from '../../../../App.module.css';
import classes from './FormatNumberWithCurrency.module.css';
import ParamsList from './params-list/ParamsList';
import CodeBlock from 'components/layout/code-block/CodeBlock';
import TestBlock from 'components/layout/test-block/TestBlock';
import { UtilsTypes } from 'constants/utilsTypes';

const FormatNumberWithCurrency = () => {
    const { DescriptionItem } = globalClasses;
    const { CustomCurrency } = classes;
    const customSettingsDescription = `
  -> custom currency symbol overrides made by currency for cases where same offering is used on multiple markets
  -> override by currency can work without override by locale
  [
    {
      "currency": "CAD",
      "symbol": "$",
    }
  ]
  -> or even more specific override by currency and locale
  -> override by locale can't work without override by currency
  [
    {
      "currency": "EUR",
      "symbol": "e",
      "locales": [
        {
          "en_GB": "eee"
        },
        {
          "en_US": "EEEEE"
        }
      ]
    }
  ]`;

    return (
        <div>
            <div className={DescriptionItem}>
                <strong>formatNumberWithCurrency()</strong>
            </div>
            <ParamsList />
            <p className={DescriptionItem}>
                <i>*required</i>
            </p>
            <div className={DescriptionItem}>
                <strong>customCurrency</strong> property - can be set in
                customer settings and has the following usage:
                <ul className={CustomCurrency}>
                    <li>
                        <b>useSymbolAsSuffix </b>
                        <i>
                            display currency symbol as suffix e.g.: default "$
                            1.00" - useSymbolAsSuffix "1.00 $"
                        </i>
                    </li>
                    <li>
                        <b>useSymbolAsPrefix </b>
                        <i>
                            display currency symbol as prefix e.g.: default
                            "1.00 $" - useSymbolAsPrefix "$ 1.00"
                        </i>
                    </li>
                    <li>
                        <b>customCurrencySymbol </b>
                        <i>
                            display custom currency symbol e.g.: default "1.00
                            $" - useSymbolAsPrefix "1.00 #"
                        </i>
                    </li>
                    <li>
                        <b>customLiteralSeparator </b>
                        <i>
                            display custom literal separator e.g.: default "1.00
                            $" - customLiteralSeparator "1.00$"
                        </i>
                        <ul>
                            <li>
                                Please note that for some currency-locale
                                combinations, the <strong>formatToParts</strong>{' '}
                                function, doesn't return a literal separator. In
                                that case, the override won't take place since
                                it will not be identified in the formatToParts
                                output. <br />
                                For more details, check &nbsp;
                                <a
                                    href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/formatToParts"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    the official documentation
                                </a>
                                . E.g.: for <strong>en-US</strong> and{' '}
                                <strong>USD</strong> currency, the literal
                                separator is not returned.
                            </li>
                        </ul>
                    </li>
                    <li>
                        <b>customDecimalSeparator </b>
                        <i>
                            display custom decimal separator e.g.: default "1.00
                            $" - customDecimalSeparator "1,00 $"
                        </i>
                    </li>
                    <li>
                        <b>customSymbolOverridesByCurrencyOrLocale </b>
                        <i>array of objects as below</i>
                    </li>
                </ul>
                <pre>
                    <code>{customSettingsDescription}</code>
                </pre>
            </div>
            <CodeBlock
                title="General input for the formatNumberWithCurrency() method"
                initialDisplayedCode={`{
                "locale": "en_GB",
                "value": 1000.00,
                "numberOfDecimals": 2,
                "currency": "EUR",
                "customCurrency": {
                    "useSymbolAsSuffix": true,
                    "useSymbolAsPrefix": false,
                    "customCurrencySymbol": "#", `}
                moreCode={`
                    "customLiteralSeparator": "",
                    "customDecimalSeparator": ",",
                    "customSymbolOverridesByCurrencyOrLocale": []
                },
                "customerSettingsData": {
                    "milliSeperator": ".",
                    "enableStakeDecimals": false
                }
            }`}
            />
            <TestBlock
                validationSchema={FormatNumberWithCurrencyTypeSchema}
                title="You can test the formatNumberWithCurrency() method by adding the input parameters as
                a JSON object in the below textarea, click Run and check the
                Output in the right"
                type={UtilsTypes.FORMAT_NUMBER_WITH_CURRENCY}
            />
        </div>
    );
};

export default FormatNumberWithCurrency;
